import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  listMySchools,
  listPayrollBatches,
  createPayrollBatch,
  deletePayrollBatch,
  getPayrollRows,
  setPayrollRows,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Thanh toán lương theo đợt (vd "Lương T07.2026"): mỗi đợt là 1 bảng các
// dòng thanh toán tự do (không cố định theo lớp) — 1 người có thể có nhiều
// dòng (lương tháng + truy lĩnh tháng trước, hoặc lương ở 2 đơn vị PCT/QTKD).

// Họ tên hiển thị/in/xuất: tiếng Việt không dấu (dữ liệu gốc vẫn giữ dấu để khớp hồ sơ).
const khongDau = (x) => String(x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");

const fmtMoney = (n) => (Number(n) || 0).toLocaleString("vi-VN");
const emptyRow = () => ({ id: "tmp_" + Math.random().toString(36).slice(2), source: "PCT", name: "", amount: "", account: "", bank: "", content: "", note: "" });

function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
function downloadWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Export lại."
    );
    return;
  }
  const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const HappySchoolPayroll = () => {
  const schoolId = getActiveSchoolId();
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [nhom, setNhom] = useState("all"); // all | GV THPT | GV Nghề | khac — lọc theo nhóm (field group)
  const [subTab, setSubTab] = useState("luong"); // "luong" | "hoanve" — tách Đối chiếu (Thực lĩnh/Hoàn về) ra khỏi bảng lương chính

  useEffect(() => {
    if (!schoolId) return;
    listMySchools()
      .then((l) => { const s = (l || []).find((x) => x.id === schoolId); setIsOwnerAdmin(!!s && (s.role === "owner" || s.role === "admin")); })
      .catch(() => {});
    reloadBatches();
  }, [schoolId]); // eslint-disable-line

  const reloadBatches = () => {
    setLoadingBatches(true);
    listPayrollBatches(schoolId)
      .then((list) => {
        setBatches(list);
        setSelectedBatchId((cur) => cur || (list[0] && list[0].id) || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingBatches(false));
  };

  useEffect(() => {
    if (!schoolId || !selectedBatchId) return;
    setLoadingRows(true);
    setError("");
    getPayrollRows(schoolId, selectedBatchId)
      // Dòng có "order" (ưu tiên, vd Ông Chung, cô Hiền, cô Diệu Linh) lên đầu; còn lại giữ thứ tự cũ.
      .then((r) => setRows((r || []).map((x, i) => ({ x, i })).sort((a, b) => (Number.isFinite(Number(a.x.order)) && a.x.order !== "" ? Number(a.x.order) : 1e9) - (Number.isFinite(Number(b.x.order)) && b.x.order !== "" ? Number(b.x.order) : 1e9) || a.i - b.i).map((o) => o.x)))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingRows(false));
  }, [schoolId, selectedBatchId]);

  const createBatch = async (e) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    setError("");
    try {
      const b = await createPayrollBatch(schoolId, newName.trim(), newNote.trim());
      setNewName("");
      setNewNote("");
      setShowNewForm(false);
      setBatches((list) => [b, ...list]);
      setSelectedBatchId(b.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const removeBatch = async (batchId) => {
    if (!window.confirm("Xoá đợt lương này? Toàn bộ dữ liệu của đợt sẽ mất, không khôi phục được.")) return;
    setError("");
    try {
      await deletePayrollBatch(schoolId, batchId);
      setBatches((list) => list.filter((b) => b.id !== batchId));
      setSelectedBatchId((cur) => (cur === batchId ? null : cur));
    } catch (err) {
      setError(err.message);
    }
  };

  const updateRow = (id, field, value) => {
    setRows((list) => list.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((list) => [...list, { ...emptyRow(), source: subTab === "hoanve" ? "Đối chiếu" : nhom === "QTKD" ? "QTKD" : "PCT", group: nhom === "all" || nhom === "khac" || String(nhom).startsWith("tra:") ? "" : nhom, ...(String(nhom).startsWith("tra:") ? { ngayTra: nhom.slice(4) } : {}) }]);
  const removeRow = (id) => setRows((list) => list.filter((r) => r.id !== id));

  const save = async () => {
    setSaving(true);
    setError("");
    setMsg("");
    try {
      const clean = rows.filter((r) => r.name.trim() || Number(r.amount) > 0);
      await setPayrollRows(schoolId, selectedBatchId, clean);
      setRows(clean);
      setMsg("Đã lưu.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Xuất Excel = ĐÚNG danh sách đang hiện ở tab (cùng danh sách/thứ tự với bản in), không kèm dòng tab khác.
  const exportExcel = () => {
    const batch = batches.find((b) => b.id === selectedBatchId);
    const list = outputList();
    const header = ["TT", "Tên người thụ hưởng", "Lương", "Số tài khoản", "Ngân hàng hưởng", "Nội dung", "Ghi chú"];
    const data = list.map((r, i) => [i + 1, khongDau(r.name), Number(r.amount) || 0, String(r.account || ""), r.bank || "", r.content || "", r.note || ""]);
    const tong = list.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data, ["Tổng:", "", tong, "", "", "", ""]]);
    const last = data.length + 1; // dòng dữ liệu cuối (1-based, dòng 1 là tiêu đề)
    if (data.length) ws[`C${last + 1}`] = { t: "n", v: tong, f: `SUM(C2:C${last})`, z: "#,##0" };
    for (let i = 2; i <= last; i++) { const c = ws[`C${i}`]; if (c) c.z = "#,##0"; const d = ws[`D${i}`]; if (d) { d.t = "s"; d.z = "@"; } }
    ws["!cols"] = [{ wch: 5 }, { wch: 26 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 40 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Luong");
    const tabTen = subTab === "hoanve" ? "Hoan ve" : nhom === "all" ? "PCT" : nhom === "khac" ? "Khac" : String(nhom).startsWith("tra:") ? `Tra ${nhom.slice(4)}` : khongDau(nhom);
    downloadWorkbook(wb, `${(batch && batch.name) || "luong"} - ${tabTen}.xlsx`);
  };

  // "Đối chiếu" (Thực lĩnh/Hoàn về) tách hẳn khỏi bảng Lương — chỉ diễn giải
  // lại PCT+QTKD, "Hoàn về" là tiền VỀ trường chứ không phải lương chi ra,
  // để chung sẽ đếm trùng/sai chiều vào Tổng cộng.
  // "all" = PCT (mọi dòng trừ QTKD); QTKD là tab riêng.
  const isQtkd = (r) => r.group === "QTKD" || (r.source === "QTKD" && !r.group);
  // Dòng có "ngayTra" (vd "25-10") = trả riêng vào ngày đó -> chỉ hiện ở tab "Trả <ngày>", không tính vào PCT/các nhóm/tổng của đợt.
  const inNhom = (r) => (String(nhom).startsWith("tra:") ? r.ngayTra === nhom.slice(4) : r.ngayTra ? false : nhom === "all" ? !isQtkd(r) : nhom === "QTKD" ? isQtkd(r) : nhom === "khac" ? !r.group && !isQtkd(r) : r.group === nhom);
  const luongAllCa = rows.filter((r) => r.source !== "Đối chiếu");
  const luongAll = luongAllCa.filter((r) => !r.ngayTra);
  const ngayTraList = [...new Set(luongAllCa.map((r) => r.ngayTra).filter(Boolean))].sort();
  const luongRows = luongAll.filter(inNhom);
  const demNhom = (k) => luongAll.filter((r) => (k === "khac" ? !r.group && !isQtkd(r) : k === "QTKD" ? isQtkd(r) : r.group === k)).length;
  const hoanVeRows = rows.filter((r) => r.source === "Đối chiếu");
  const visibleRows = subTab === "hoanve" ? hoanVeRows : luongRows;

  const totalBySource = luongRows.reduce((m, r) => {
    const key = r.source || "Khác";
    m[key] = (m[key] || 0) + (Number(r.amount) || 0);
    return m;
  }, {});
  const total = luongRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const thucLinhTotal = hoanVeRows.filter((r) => (r.content || "").includes("Thực lĩnh")).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const hoanVeTotal = hoanVeRows.filter((r) => (r.content || "").includes("Hoàn về")).reduce((s, r) => s + (Number(r.amount) || 0), 0);

  // 🖨 In danh sách đang hiện (theo tab + nhóm đang lọc).
  // Danh sách dùng chung cho In và Xuất Excel: đúng các dòng đang hiện ở tab; tab PCT thì Agribank lên trước.
  function outputList() {
    let list = visibleRows.filter((r) => (r.name || "").trim() || Number(r.amount) > 0);
    if (subTab !== "hoanve" && nhom === "all") {
      const agri = (r) => /agri/i.test(String(r.bank || "").normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
      list = [...list.filter(agri), ...list.filter((r) => !agri(r))];
    }
    return list;
  }
  const printList = () => {
    const batch = batches.find((b) => b.id === selectedBatchId);
    const esc = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const money = (v) => Math.round(Number(v) || 0).toLocaleString("vi-VN");
    const list = outputList();
    const tong = list.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const nhomLabel = subTab === "hoanve" ? " — ĐỐI CHIẾU / HOÀN VỀ" : nhom === "all" ? " — PCT" : nhom === "khac" ? " — KHÁC" : String(nhom).startsWith("tra:") ? ` — TRẢ NGÀY ${nhom.slice(4)}` : ` — ${nhom.toUpperCase()}`;
    const body = list.map((r, i) => `<tr><td class="c">${i + 1}</td><td class="l b">${esc(khongDau(r.name))}</td><td class="b">${money(r.amount)}</td><td class="c">${esc(r.account)}</td><td class="c">${esc(r.bank)}</td><td class="l">${esc(r.content)}</td><td class="c">${esc(r.note)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc((batch && batch.name) || "Lương")}</title>
<style>@page{size:A4 portrait;margin:12mm}body{font-family:"Times New Roman",serif;font-size:12px;color:#000}h2{text-align:center;margin:4px 0}
table{border-collapse:collapse;width:100%}th,td{border:1px solid #000;padding:3px 5px;text-align:right}th{background:#eee;text-align:center}
td.l{text-align:left}td.c{text-align:center}.b{font-weight:bold}tr{page-break-inside:avoid}thead{display:table-header-group}.tot td{background:#f3f3f3;font-weight:bold}
.sign2{width:45%;margin:18px 0 0 auto;text-align:center;font-size:14px;line-height:1.6}</style></head><body>
${subTab !== "hoanve" && nhom === "QTKD" ? "" : '<div style="font-weight:bold">TRƯỜNG TRUNG CẤP KINH TẾ - KỸ THUẬT PHAN CHU TRINH</div>'}
<h2>DANH SÁCH CHUYỂN KHOẢN — ${esc(((batch && batch.name) || "").toUpperCase())}${esc(nhomLabel)}</h2>
<table><thead><tr><th>STT</th><th>Họ và tên</th><th>Số tiền</th><th>Số tài khoản</th><th>Ngân hàng</th><th>Nội dung</th><th>Ghi chú</th></tr></thead>
<tbody>${body}<tr class="tot"><td colspan="2" class="l">Tổng cộng (${list.length} người)</td><td>${money(tong)}</td><td colspan="4"></td></tr></tbody></table>
<div class="sign2"><i>Hà Nội, ngày ...... tháng ...... năm ${esc(((((batch && batch.name) || "").match(/(20\d{2})/) || [])[1]) || new Date().getFullYear())}</i><br>${subTab !== "hoanve" && nhom === "QTKD" ? "<b>KT. HIỆU TRƯỞNG<br>PHÓ HIỆU TRƯỞNG</b>" : "<b>Chủ tài khoản/Người được ủy quyền</b><br><i>(Ký, ghi rõ họ tên)</i>"}</div>
<script>window.onload=function(){setTimeout(function(){window.print()},200)}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { window.alert("Trình duyệt chặn cửa sổ in — cho phép popup rồi bấm lại."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  if (!schoolId) return <p style={{ color: "#6b7280" }}>Chưa chọn trường.</p>;

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      <div style={{ width: 240, flexShrink: 0 }}>
        <h4 style={{ margin: "0 0 8px" }}>Các đợt lương</h4>
        {loadingBatches ? (
          <p style={{ color: "#6b7280", fontSize: 13 }}>Đang tải...</p>
        ) : (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
            {batches.length === 0 && <div style={{ padding: 10, color: "#6b7280", fontSize: 13 }}>Chưa có đợt lương nào.</div>}
            {batches.map((b) => (
              <div
                key={b.id}
                onClick={() => setSelectedBatchId(b.id)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f1f5f9",
                  background: selectedBatchId === b.id ? "#eff6ff" : "#fff",
                  fontWeight: selectedBatchId === b.id ? 600 : 400,
                  fontSize: 13,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{b.name}</span>
                {isOwnerAdmin && (
                  <span
                    onClick={(e) => { e.stopPropagation(); removeBatch(b.id); }}
                    style={{ color: "#dc2626", fontSize: 12, marginLeft: 8 }}
                    title="Xoá đợt"
                  >
                    ✕
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {isOwnerAdmin && !showNewForm && (
          <button type="button" onClick={() => setShowNewForm(true)} style={ghostBtn}>+ Tạo đợt mới</button>
        )}
        {isOwnerAdmin && showNewForm && (
          <form onSubmit={createBatch} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <input placeholder="Tên đợt (vd: Lương T08.2026)" value={newName} onChange={(e) => setNewName(e.target.value)} style={inputStyle} autoFocus />
            <input placeholder="Ghi chú (không bắt buộc)" value={newNote} onChange={(e) => setNewNote(e.target.value)} style={inputStyle} />
            <div style={{ display: "flex", gap: 6 }}>
              <button type="submit" disabled={creating || !newName.trim()} style={applyBtn}>{creating ? "Đang tạo..." : "Tạo"}</button>
              <button type="button" onClick={() => setShowNewForm(false)} style={ghostBtn}>Huỷ</button>
            </div>
          </form>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {error && <p style={{ color: "#dc2626" }}>{error}</p>}
        {msg && <p style={{ color: "#059669" }}>{msg}</p>}
        {!selectedBatchId ? (
          <p style={{ color: "#6b7280" }}>Chọn hoặc tạo một đợt lương ở bên trái.</p>
        ) : loadingRows ? (
          <p>Đang tải...</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              <button type="button" onClick={() => setSubTab("luong")} style={subTab === "luong" ? tabActive : tabInactive}>💵 Lương ({luongRows.length})</button>
              <button type="button" onClick={() => setSubTab("hoanve")} style={subTab === "hoanve" ? tabActive : tabInactive}>↩️ Hoàn về ({hoanVeRows.length})</button>
            </div>

            {subTab === "luong" && (
              <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                {[["all", `PCT (${luongAll.filter((r) => !isQtkd(r)).length})`], ["GV THPT", `GV THPT (${demNhom("GV THPT")})`], ["GV Nghề", `GV Nghề (${demNhom("GV Nghề")})`], ["Đào tạo", `Đào tạo (${demNhom("Đào tạo")})`], ...(demNhom("Lái xe") ? [["Lái xe", `Lái xe (${demNhom("Lái xe")})`]] : []), ["QTKD", `QTKD (${demNhom("QTKD")})`], ...(demNhom("khac") ? [["khac", `Khác (${demNhom("khac")})`]] : []), ...ngayTraList.map((d) => [`tra:${d}`, `📅 Trả ${d} (${luongAllCa.filter((r) => r.ngayTra === d).length})`])].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setNhom(k)} style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${nhom === k ? "#0f766e" : "#d1d5db"}`, background: nhom === k ? "#0f766e" : "#fff", color: nhom === k ? "#fff" : "#374151", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{l}</button>
                ))}
              </div>
            )}
            {subTab === "luong" ? (
              <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                {Object.entries(totalBySource).map(([src, amt]) => (
                  <div key={src} style={statBox}>Tổng {src}: <b>{fmtMoney(amt)}</b></div>
                ))}
                <div style={{ ...statBox, background: "#ecfdf5" }}>Tổng cộng: <b>{fmtMoney(total)}</b> ({luongRows.length} dòng)</div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                <div style={statBox}>Tổng Thực lĩnh: <b>{fmtMoney(thucLinhTotal)}</b></div>
                <div style={{ ...statBox, background: "#fef2f2" }}>Tổng Hoàn về: <b>{fmtMoney(hoanVeTotal)}</b></div>
              </div>
            )}
            {subTab === "hoanve" && (
              <p style={{ fontSize: 12, color: "#92400e", marginTop: -6, marginBottom: 12 }}>
                * Tab này chỉ diễn giải lại số PCT+QTKD cho người có lương ở 2 nguồn — không cộng vào Tổng cộng bên tab Lương. "Hoàn về" là tiền về trường, không phải lương chi ra.
              </p>
            )}
            <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12 }}>
              <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={th}>Nguồn</th>
                    <th style={{ ...th, textAlign: "left" }}>Họ tên</th>
                    <th style={th}>Số tiền</th>
                    <th style={th}>Số tài khoản</th>
                    <th style={th}>Ngân hàng</th>
                    <th style={{ ...th, textAlign: "left" }}>Nội dung</th>
                    <th style={{ ...th, textAlign: "left" }}>Ghi chú</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r.id}>
                      <td style={td}>
                        <input value={r.source || ""} onChange={(e) => updateRow(r.id, "source", e.target.value)} style={{ ...cellInput, width: 60 }} />
                      </td>
                      <td style={td}>
                        <input value={khongDau(r.name)} onChange={(e) => updateRow(r.id, "name", e.target.value)} style={{ ...cellInput, width: 160, textAlign: "left" }} />
                      </td>
                      <td style={td}>
                        <input type="number" min={0} value={r.amount ?? ""} onChange={(e) => updateRow(r.id, "amount", e.target.value)} style={{ ...cellInput, width: 100, textAlign: "right" }} />
                      </td>
                      <td style={td}>
                        <input value={r.account || ""} onChange={(e) => updateRow(r.id, "account", e.target.value)} style={{ ...cellInput, width: 140 }} />
                      </td>
                      <td style={td}>
                        <input value={r.bank || ""} onChange={(e) => updateRow(r.id, "bank", e.target.value)} style={{ ...cellInput, width: 100 }} />
                      </td>
                      <td style={td}>
                        <input value={r.content || ""} onChange={(e) => updateRow(r.id, "content", e.target.value)} style={{ ...cellInput, width: 180, textAlign: "left" }} />
                      </td>
                      <td style={td}>
                        <input value={r.note || ""} onChange={(e) => updateRow(r.id, "note", e.target.value)} style={{ ...cellInput, width: 100, textAlign: "left" }} />
                      </td>
                      <td style={td}>
                        <span onClick={() => removeRow(r.id)} style={{ color: "#dc2626", cursor: "pointer" }} title="Xoá dòng">✕</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={addRow} style={ghostBtn}>+ Thêm dòng</button>
              <button type="button" onClick={save} disabled={saving} style={applyBtn}>{saving ? "Đang lưu..." : "💾 Lưu"}</button>
              <button type="button" onClick={exportExcel} style={ghostBtn}>⬇ Export</button>
              <button type="button" onClick={printList} style={ghostBtn} disabled={visibleRows.length === 0}>🖨 In</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const statBox = { padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", fontSize: 13 };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };
const cellInput = { padding: 5, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: 100 };
const inputStyle = { padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 };
const applyBtn = { padding: "10px 18px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const ghostBtn = { padding: "10px 18px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabInactive = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabActive = { ...tabInactive, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };

export default HappySchoolPayroll;
